// @flow
import { SectionSteps } from 'hooks/sectionsProgress/types';
import PATHS, { WAGES_AND_INCOME_PATHS } from 'constants/paths';

import { areAllChoicesNo } from './utils';
import { NO_INCOME_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.noIncome';
import { CURRENT_JOB_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.currentJob';
import { ANTICIPATED_JOB_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.anticipatedJob';
import { LAST_JOB_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.lastJob';
import { CHILD_SUPPORT_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.childSupport';
import { ALIMONY_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.alimony';
import { SOCIAL_SECURITY_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.socialSec';
import { OTHER_INCOME_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.otherIncome';
import { PUBLIC_AID_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.publicAid';
import { GIFT_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.gifts';
import { UNEMPLOYMENT_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.unemployment';
import { PENSION_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.pension';
import { ONLINE_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.online';
import { TRUST_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.trust';
import { WAGES_AND_INCOME_SECTION_NAMES } from 'pages/AffordaWagesAndIncome/constants';
import { BENEFITS_RENTAL_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.benefitsRental';
import { CHOICE_NAMES } from 'pages/AffordaWagesAndIncome/constants.choices';

// Only adding optional forms steps fields here. Can change this later if needed.
export const WAGES_AND_INCOME_PATHS_TO_FIELD_NAMES_MAP = {
  [WAGES_AND_INCOME_PATHS.NO_INCOME]: NO_INCOME_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.CURRENT_JOB]: CURRENT_JOB_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.ANTICIPATED_JOB]: ANTICIPATED_JOB_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.LAST_JOBS]: LAST_JOB_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.CHILD_SUPPORT]: CHILD_SUPPORT_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.ALIMONY]: ALIMONY_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.SOCIAL_SECURITY]: SOCIAL_SECURITY_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.OTHER_INCOME]: OTHER_INCOME_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.PUBLIC_ASSISTANCE]: PUBLIC_AID_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.GIFTS]: GIFT_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.UNEMPLOYMENT]: UNEMPLOYMENT_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.PENSION]: PENSION_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.ONLINE]: ONLINE_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.BENEFITS_RENTAL]: BENEFITS_RENTAL_FIELD_NAMES,
  [WAGES_AND_INCOME_PATHS.TRUST]: TRUST_FIELD_NAMES,
};

const WAGES_AND_INCOME_STEPS = ({ choices }): SectionSteps => {
  const allChoicesNo = areAllChoicesNo(choices);
  return [
    {
      name: WAGES_AND_INCOME_PATHS.OVERVIEW,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.OVERVIEW,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.OVERVIEW}`,
      required: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.CHOICES_1,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.CHOICES,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.CHOICES_1}`,
      required: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.CHOICES_2,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.CHOICES,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.CHOICES_2}`,
      required: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.CHOICES_3,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.CHOICES,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.CHOICES_3}`,
      required: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.CHOICES_4,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.CHOICES,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.CHOICES_4}`,
      required: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.NO_INCOME,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.NO_INCOME,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.NO_INCOME}`,
      required: allChoicesNo,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.CURRENT_JOB,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.CURRENT_JOBS,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.CURRENT_JOB}`,
      required:
        choices?.[CHOICE_NAMES.CURRENT_JOB]?.toLowerCase() === 'yes' || null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.ANTICIPATED_JOB,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.ANTICIPATED_JOBS,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.ANTICIPATED_JOB}`,
      required:
        choices?.[CHOICE_NAMES.ANTICIPATE_JOB_12_MONTHS]?.toLowerCase() ===
          'yes' || null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.LAST_JOBS,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.LAST_JOBS,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.LAST_JOBS}`,
      required:
        choices?.[CHOICE_NAMES.JOB_LAST_90_DAYS]?.toLowerCase() === 'yes' ||
        null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.CHILD_SUPPORT,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.CHILD_SUPPORTS,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.CHILD_SUPPORT}`,
      required:
        choices?.[CHOICE_NAMES.CHILD_SUPPORT_ELIGIBLE]?.toLowerCase() ===
          'yes' || null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.ALIMONY,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.ALIMONIES,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.ALIMONY}`,
      required:
        choices?.[CHOICE_NAMES.ALIMONY_PAYMENTS]?.toLowerCase() === 'yes' ||
        null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.SOCIAL_SECURITY,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.SOCIAL_SECS,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.SOCIAL_SECURITY}`,
      required: choices?.[CHOICE_NAMES.SSI]?.toLowerCase() === 'yes' || null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.OTHER_INCOME,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.OTHER_INCOMES,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.OTHER_INCOME}`,
      required:
        choices?.[CHOICE_NAMES.PAYMENTS_OTHER]?.toLowerCase() === 'yes' || null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.PUBLIC_ASSISTANCE,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.PUBLIC_ASSISTANCES,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.PUBLIC_ASSISTANCE}`,
      required:
        choices?.[CHOICE_NAMES.BENEFITS_SNAPS]?.toLowerCase() === 'yes' || null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.GIFTS,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.GIFTS,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.GIFTS}`,
      required:
        choices?.[CHOICE_NAMES.BENEFITS_FAMILY]?.toLowerCase() === 'yes' ||
        null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.UNEMPLOYMENT,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.UNEMPLOYMENTS,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.UNEMPLOYMENT}`,
      required:
        choices?.[CHOICE_NAMES.BENEFITS_DISABILITY]?.toLowerCase() === 'yes' ||
        null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.PENSION,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.PENSIONS,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.PENSION}`,
      required:
        choices?.[CHOICE_NAMES.BENEFITS_PENSION]?.toLowerCase() === 'yes' ||
        null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.VA_BENEFITS,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.VA_BENEFITS,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.VA_BENEFITS}`,
      required: choices?.choicesBenefitsVa?.toLowerCase() === 'yes' || null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.ONLINE,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.ONLINES,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.ONLINE}`,
      required:
        choices?.[CHOICE_NAMES.PAYMENTS_ONLINE]?.toLowerCase() === 'yes' ||
        null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.TRUST,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.TRUSTS,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.TRUST}`,
      required:
        choices?.[CHOICE_NAMES.PAYMENTS_TRUST]?.toLowerCase() === 'yes' || null,
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.BENEFITS_RENTAL,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.BENEFITS_RENTAL,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.BENEFITS_RENTAL}`,
      required:
        choices?.[CHOICE_NAMES.BENEFITS_RENTAL]?.toLowerCase() === 'yes',
      optional: true,
      complete: false,
    },
    {
      name: WAGES_AND_INCOME_PATHS.SUMMARY,
      sectionName: WAGES_AND_INCOME_SECTION_NAMES.SUMMARY,
      path: `${PATHS.WAGES_AND_INCOME}/${WAGES_AND_INCOME_PATHS.SUMMARY}`,
      required: true,
      complete: false,
    },
  ];
};

export default WAGES_AND_INCOME_STEPS;
